import React, { useEffect, useRef, useCallback, useState } from "react";
import { Loader2, User } from "lucide-react";
import { RootState, useAppDispatch } from "../../../redux/store";
import { getNotificationDetail } from "../../../redux/features/notifications";
import { getNotifications, getNotificationCategories, clearMobileNotifications } from "../../../redux/features/mobileNotifications";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { useNavigate } from "react-router";
import { setShowTopNav } from "../../../redux/features/mobileLayout";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout/slice";
import { decrementUnreadNotifCount } from "../../../redux/features/common/slice";
import { DateTimeUtil, getNotificationStatusLabel } from "../../../utils";
import { MobileNotification } from "../../../types";
import { setSelectedCategoryForDetails } from "../../../redux/features/notifications/slice";
import { setSelectedNotification } from "../../../redux/features/mobileNotifications";
import PullToRefresh from "pulltorefreshjs";
import { TimeoffNavTitle } from "../../../utils/timeoff";
import { notificationComponents } from "../../../config";

const NotificationsMobile: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { isNotificationsLoading, notifications, notificationsMeta, isCategoriesLoading, categories } = useSelector(
        (state: RootState) => state.mobileNotifications
    );

    // Debug log for state changes
    useEffect(() => {
        console.log('[State] Notifications state updated:', {
            count: notifications.length,
            meta: notificationsMeta,
            isLoading: isNotificationsLoading
        });
    }, [notifications.length, notificationsMeta, isNotificationsLoading]);
    const user = useSelector((state: RootState) => state.user.user);

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
    const isLoadingMoreRef = useRef(false);
    const loadMoreCallbackRef = useRef<() => void>();
    const lastScrollTimeRef = useRef(0);
    const hasReachedEndRef = useRef(false); // Track if we've determined there are no more items
    const scrollPositionBeforeLoadRef = useRef<number>(0); // Track scroll position before loading more
    const lastLoadCompleteTimeRef = useRef<number>(0); // Track when loading completed to prevent immediate re-triggers

    // Filter states
    const [selectedFilter, setSelectedFilter] = useState<"all" | "unread" | "read">("all");
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
    const [hasReachedEnd, setHasReachedEnd] = useState(false);

    const refreshData = useCallback(async () => {
        // Clear existing notifications before fetching new filtered results
        dispatch(clearMobileNotifications());
        
        // Build API params based on selected filters
        const params: any = { page: 1 };
        
        // Add notification_status if filter is not "all"
        if (selectedFilter === "unread") {
            params.notification_status = "0";
        } else if (selectedFilter === "read") {
            params.notification_status = "1";
        }
        
        // Add category if selected
        if (selectedCategory) {
            params.category = selectedCategory;
        }
        
        await dispatch(getNotifications(params));
    }, [dispatch, selectedFilter, selectedCategory]);

    useEffect(() => {
        console.log('[Mount] Component mounted');
        console.log('[Mount] Initial state:', {
            notificationsCount: notifications.length,
            notificationsMeta,
            isNotificationsLoading
        });
        
        dispatch(setShowTopNav(false));
        
        // Check if categories are cached in localStorage
        const cachedCategories = localStorage.getItem('notificationCategories');
        if (!cachedCategories) {
            // Only fetch from server if not cached
            dispatch(getNotificationCategories());
        }
        
        refreshData();

        PullToRefresh.init({
            mainElement: "#notif-refresh-container",
            onRefresh: () => refreshData(),
            instructionsPullToRefresh: "Pull to refresh",
            instructionsReleaseToRefresh: "Release to refresh",
            getMarkup: () => `
                <div class="w-full h-16 flex items-center justify-center bg-white shadow-sm">
                    <div class="w-full flex items-center justify-center">
                    <div class="flex items-center justify-center gap-2 text-sm text-gray-600">
                        <div class="h-4 w-4 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></div>
                        <span>Refreshing...</span>
                    </div>
                    </div>
                </div>
                `,
            distThreshold: 40,
            distMax: 60,
            distReload: 50,
            shouldPullToRefresh() {
                const container = document.getElementById("notif-refresh-container");
                return container?.scrollTop === 0;
            },
        });

        return () => {
            PullToRefresh.destroyAll();
        };
    }, [refreshData]);

    // Reload notifications when filters change
    useEffect(() => {
        console.log('[Filters] Filter changed:', { selectedFilter, selectedCategory });
        isLoadingMoreRef.current = false; // Reset loading flag when filters change
        hasReachedEndRef.current = false; // Reset end flag when filters change
        setHasReachedEnd(false); // Reset end state when filters change
        lastLoadCompleteTimeRef.current = 0; // Reset cooldown when filters change
        refreshData();
    }, [selectedFilter, selectedCategory]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (isCategoryDropdownOpen && !target.closest(".category-dropdown-container")) {
                setIsCategoryDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isCategoryDropdownOpen]);

    // Update the callback ref whenever dependencies change
    useEffect(() => {
        loadMoreCallbackRef.current = () => {
            console.log('[LoadMore] Function called');
            console.log('[LoadMore] isLoadingMoreRef:', isLoadingMoreRef.current);
            console.log('[LoadMore] isNotificationsLoading:', isNotificationsLoading);
            console.log('[LoadMore] hasReachedEndRef:', hasReachedEndRef.current);
            
            // Prevent multiple simultaneous calls or if we've reached the end
            if (isLoadingMoreRef.current || isNotificationsLoading || hasReachedEndRef.current) {
                console.log('[LoadMore] Already loading or reached end, skipping');
                return;
            }
            
            const currentPage = notificationsMeta?.page ?? 1;
            const nextPage = currentPage + 1;
            const loadedCount = notifications.length;
            const totalCount = notificationsMeta?.count ?? 0;
            const limit = notificationsMeta?.limit ?? 50;
            const itemsOnCurrentPage = notifications.length;

            console.log('[LoadMore] Page info:', { 
                currentPage,
                nextPage, 
                loadedCount, 
                totalCount,
                limit,
                itemsOnCurrentPage,
                fullMeta: notificationsMeta
            });

            // Check if there are more items to load
            // Strategy: If we got exactly 'limit' items on current page, try loading next page
            // The API might return count as total available, but if current page is full, there might be more
            const receivedFullPage = itemsOnCurrentPage >= limit;
            const hasMoreByCount = loadedCount < totalCount;
            
            // If we got a full page of results, assume there might be more pages
            // This handles cases where count might not accurately reflect total available
            const shouldLoadMore = hasMoreByCount || receivedFullPage;
            
            console.log('[LoadMore] Has more checks:', {
                hasMoreByCount,
                receivedFullPage,
                itemsOnCurrentPage,
                limit,
                totalCount,
                loadedCount,
                shouldLoadMore
            });

            // Only load more if we should
            if (!shouldLoadMore) {
                console.log('[LoadMore] All items loaded, marking as reached end');
                hasReachedEndRef.current = true;
                return;
            }
            
            console.log('[LoadMore] Loading page', nextPage);
            isLoadingMoreRef.current = true;

            // Save current scroll position before loading
            const container = scrollRef.current;
            if (container) {
                scrollPositionBeforeLoadRef.current = container.scrollTop;
            }

            // Build API params based on selected filters
            const params: any = { page: nextPage };
            
            if (selectedFilter === "unread") {
                params.notification_status = "0";
            } else if (selectedFilter === "read") {
                params.notification_status = "1";
            }
            
            if (selectedCategory) {
                params.category = selectedCategory;
            }
            
            console.log('[LoadMore] API params:', params);
            
            dispatch(getNotifications(params)).then((result: any) => {
                console.log('[LoadMore] Request completed');
                isLoadingMoreRef.current = false;
                
                // Set cooldown period - prevent triggers for 1 second after loading completes
                lastLoadCompleteTimeRef.current = Date.now();
                
                // Adjust scroll position after loading to prevent immediate re-triggering
                // Use double requestAnimationFrame to ensure DOM has fully updated with new items
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (container) {
                            const { scrollHeight, clientHeight, scrollTop } = container;
                            const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
                            const isNearBottom = distanceFromBottom < 300;
                            
                            // Only adjust if we're still near the bottom (which would trigger loadMore again)
                            if (isNearBottom) {
                                // Scroll up enough to move sentinel out of view (200px buffer)
                                // This prevents the intersection observer from immediately triggering again
                                const newScrollTop = scrollHeight - clientHeight - 200;
                                if (newScrollTop >= 0) {
                                    container.scrollTop = newScrollTop;
                                    console.log('[LoadMore] Adjusted scroll position to prevent re-triggering:', {
                                        oldScrollTop: scrollPositionBeforeLoadRef.current,
                                        newScrollTop,
                                        scrollHeight,
                                        clientHeight,
                                        distanceFromBottom: scrollHeight - (newScrollTop + clientHeight)
                                    });
                                }
                            }
                        }
                    });
                });
                
                // Check if we got fewer items than limit - means we've reached the end
                if (result.payload?.data) {
                    const itemsReturned = result.payload.data.length;
                    const limit = result.payload.meta?.limit ?? 50;
                    
                    console.log('[LoadMore] Response check:', {
                        itemsReturned,
                        limit,
                        reachedEnd: itemsReturned < limit
                    });
                    
                    if (itemsReturned < limit) {
                        console.log('[LoadMore] Got fewer items than limit, reached end');
                        hasReachedEndRef.current = true;
                        setHasReachedEnd(true);
                    }
                }
            }).catch(() => {
                console.log('[LoadMore] Request failed');
                isLoadingMoreRef.current = false;
                lastLoadCompleteTimeRef.current = Date.now(); // Set cooldown even on error
            });
        };
    }, [dispatch, notificationsMeta, notifications, isNotificationsLoading, selectedFilter, selectedCategory]);

    // Set up scroll event listener - stable reference
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) {
            console.log('[Scroll] Container not found');
            return;
        }

        console.log('[Scroll] Setting up scroll listener on container:', container);
        console.log('[Scroll] Container dimensions:', {
            scrollHeight: container.scrollHeight,
            clientHeight: container.clientHeight,
            scrollTop: container.scrollTop
        });

        const scrollHandler = () => {
            // Throttle: only check every 100ms
            const now = Date.now();
            if (now - lastScrollTimeRef.current < 100) return;
            lastScrollTimeRef.current = now;
            
            // Check cooldown period - prevent triggers within 1 second of last load completion
            const timeSinceLastLoad = now - lastLoadCompleteTimeRef.current;
            if (timeSinceLastLoad < 1000) {
                console.log('[Scroll] Skipping - within cooldown period:', timeSinceLastLoad);
                return;
            }
            
            if (isLoadingMoreRef.current || isNotificationsLoading || hasReachedEndRef.current) {
                console.log('[Scroll] Skipping - already loading or reached end');
                return;
            }
            
            const { scrollTop, scrollHeight, clientHeight } = container;
            const threshold = 200;
            const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
            
            console.log('[Scroll] Scroll event:', {
                scrollTop,
                scrollHeight,
                clientHeight,
                distanceFromBottom,
                threshold,
                isNearBottom: distanceFromBottom <= threshold
            });
            
            if (distanceFromBottom <= threshold) {
                // Get fresh values from state
                const currentNotifications = notifications;
                const currentMeta = notificationsMeta;
                const loadedCount = currentNotifications.length;
                const totalCount = currentMeta?.count ?? 0;
                
                const currentPage = currentMeta?.page ?? 1;
                const limit = currentMeta?.limit ?? 50;
                const itemsOnCurrentPage = currentNotifications.length;
                
                // If we got a full page, assume there might be more
                const receivedFullPage = itemsOnCurrentPage >= limit;
                const hasMoreByCount = loadedCount < totalCount;
                const shouldLoadMore = (hasMoreByCount || receivedFullPage) && !hasReachedEndRef.current;
                
                console.log('[Scroll] Near bottom - checking conditions:', {
                    loadedCount,
                    totalCount,
                    currentPage,
                    limit,
                    itemsOnCurrentPage,
                    receivedFullPage,
                    hasMoreByCount,
                    shouldLoadMore,
                    hasReachedEnd: hasReachedEndRef.current,
                    hasCallback: !!loadMoreCallbackRef.current,
                    fullMeta: currentMeta
                });
                
                if (shouldLoadMore && loadMoreCallbackRef.current) {
                    console.log('[Scroll] Triggering loadMore');
                    loadMoreCallbackRef.current();
                } else {
                    console.log('[Scroll] Not loading - conditions not met:', {
                        shouldLoadMore,
                        hasReachedEnd: hasReachedEndRef.current,
                        hasCallback: !!loadMoreCallbackRef.current,
                        reason: !shouldLoadMore ? 'no more items or reached end' : 'no callback'
                    });
                }
            }
        };

        container.addEventListener("scroll", scrollHandler, { passive: true });
        container.addEventListener("touchmove", scrollHandler, { passive: true });
        console.log('[Scroll] Event listeners attached');
        
        return () => {
            console.log('[Scroll] Cleaning up event listeners');
            container.removeEventListener("scroll", scrollHandler);
            container.removeEventListener("touchmove", scrollHandler);
        };
    }, [notifications.length, notificationsMeta?.count, isNotificationsLoading]);

    // IntersectionObserver - set up when sentinel exists
    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        const container = scrollRef.current;
        
        console.log('[IntersectionObserver] Setting up observer:', {
            hasSentinel: !!sentinel,
            hasContainer: !!container,
            sentinelElement: sentinel,
            containerElement: container
        });
        
        if (!sentinel || !container) {
            console.log('[IntersectionObserver] Missing sentinel or container, skipping');
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                console.log('[IntersectionObserver] Entry:', {
                    isIntersecting: entry.isIntersecting,
                    intersectionRatio: entry.intersectionRatio,
                    boundingClientRect: entry.boundingClientRect,
                    rootBounds: entry.rootBounds
                });
                
                if (entry.isIntersecting && loadMoreCallbackRef.current) {
                    console.log('[IntersectionObserver] Sentinel is intersecting');
                    
                    // Check cooldown period - prevent triggers within 1 second of last load completion
                    const now = Date.now();
                    const timeSinceLastLoad = now - lastLoadCompleteTimeRef.current;
                    if (timeSinceLastLoad < 1000) {
                        console.log('[IntersectionObserver] Skipping - within cooldown period:', timeSinceLastLoad);
                        return;
                    }
                    
                    if (!isLoadingMoreRef.current && !isNotificationsLoading && !hasReachedEndRef.current) {
                        const loadedCount = notifications.length;
                        const totalCount = notificationsMeta?.count ?? 0;
                        const currentPage = notificationsMeta?.page ?? 1;
                        const limit = notificationsMeta?.limit ?? 50;
                        const itemsOnCurrentPage = notifications.length;
                        const totalPossibleItems = (currentPage * limit);
                        
                        console.log('[IntersectionObserver] Checking conditions:', {
                            loadedCount,
                            totalCount,
                            currentPage,
                            limit,
                            totalPossibleItems,
                            hasMoreByCount: loadedCount < totalCount,
                            hasMoreByPage: itemsOnCurrentPage >= limit, // If we got a full page, there might be more
                            isLoadingMore: isLoadingMoreRef.current,
                            isNotificationsLoading,
                            fullMeta: notificationsMeta
                        });
                        
                        // If we got a full page, assume there might be more
                        const receivedFullPage = itemsOnCurrentPage >= limit;
                        const hasMoreByCount = loadedCount < totalCount;
                        const shouldLoadMore = hasMoreByCount || receivedFullPage;
                        
                        console.log('[IntersectionObserver] Final check:', {
                            receivedFullPage,
                            hasMoreByCount,
                            shouldLoadMore
                        });
                        
                        if (shouldLoadMore) {
                            console.log('[IntersectionObserver] Triggering loadMore');
                            loadMoreCallbackRef.current();
                        } else {
                            console.log('[IntersectionObserver] All items loaded, skipping. Reason:', {
                                loadedEqualsTotal: loadedCount >= totalCount,
                                pageNotFull: itemsOnCurrentPage < limit,
                                totalCount,
                                loadedCount
                            });
                        }
                    } else {
                        console.log('[IntersectionObserver] Already loading, skipping');
                    }
                }
            },
            {
                root: container,
                rootMargin: "100px",
                threshold: 0.01,
            }
        );

        observer.observe(sentinel);
        console.log('[IntersectionObserver] Observer attached to sentinel');

        return () => {
            console.log('[IntersectionObserver] Cleaning up observer');
            observer.disconnect();
        };
    }, [notifications.length, notificationsMeta?.count, isNotificationsLoading]);

    const handleNotificationClick = (notification: MobileNotification) => {
        dispatch(setShowTopNav(true));
        dispatch(decrementUnreadNotifCount());
        dispatch(setSelectedCategoryForDetails(notification.notification_category));
        const isUnmapped = !notification.notification_category || !notificationComponents[notification.notification_category];
        // For unmapped categories or OPEN_SHIFT_AVAILABLE, use the listing notification data directly.
        if (isUnmapped || notification.notification_category === "OPEN_SHIFT_AVAILABLE") {
            dispatch(setSelectedNotification(notification));
        } else {
            dispatch(
                getNotificationDetail({
                    notificationId: notification.notification_id!,
                    employeeId: user.id!,
                })
            );
        }
        dispatch(
            setTopNavNameTitle(TimeoffNavTitle[notification.notification_category ?? ""] ?? "Notification Detail")
            // setTopNavNameTitle(notification.notification_display_name ?? "Notification Detail")
        );
        navigate("/m-notification-detail");
    };

    return (
        <div
            id="notif-refresh-container"
            ref={scrollRef}
            className="max-w-md mx-auto h-screen overflow-y-auto border rounded-lg shadow-md bg-white"
        >
            {/* Filter Section - WhatsApp Style */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 p-3">
                {/* All filters in one row */}
                <div className="flex gap-2 items-center flex-wrap">
                    <button
                        onClick={() => setSelectedFilter("all")}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                            selectedFilter === "all"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => setSelectedFilter("unread")}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                            selectedFilter === "unread"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                    >
                        Unread
                    </button>
                    <button
                        onClick={() => setSelectedFilter("read")}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                            selectedFilter === "read"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                    >
                        Read
                    </button>

                    {/* Category Button with Dropdown - Same row */}
                    <div className="relative category-dropdown-container inline-block">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
                            }}
                            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
                                selectedCategory
                                    ? "bg-blue-100 text-blue-700"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                            disabled={isCategoriesLoading}
                        >
                            <span>
                                {selectedCategory
                                    ? categories.find((c) => c.code === selectedCategory)?.name || "All Categories"
                                    : "All Categories"}
                            </span>
                            <svg
                                className={`w-4 h-4 transition-transform ${isCategoryDropdownOpen ? "rotate-180" : ""}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {/* Dropdown Menu */}
                        {isCategoryDropdownOpen && (
                            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[200px] max-h-60 overflow-y-auto z-30">
                                <button
                                    onClick={() => {
                                        setSelectedCategory("");
                                        setIsCategoryDropdownOpen(false);
                                    }}
                                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                                        !selectedCategory ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-700"
                                    }`}
                                >
                                    All Categories
                                </button>
                                {categories.map((cat) => (
                                    <button
                                        key={cat.code}
                                        onClick={() => {
                                            setSelectedCategory(cat.code);
                                            setIsCategoryDropdownOpen(false);
                                        }}
                                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                                            selectedCategory === cat.code
                                                ? "bg-blue-50 text-blue-700 font-medium"
                                                : "text-gray-700"
                                        }`}
                                    >
                                        {cat.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {isNotificationsLoading && notifications.length === 0 ? (
                <div className="flex justify-center items-center h-20">
                    <Loader2 className="w-8 h-8 text-gray-600 animate-spin" />
                </div>
            ) : notifications.length === 0 ? (
                <p className="text-gray-500 text-center p-4">No notifications found.</p>
            ) : (
                <React.Fragment>
                    <ul className="divide-y divide-gray-200">
                        {notifications.map((notification, index) => {
                            const mi = notification.message_info as any;
                            const title = mi?.title || mi?.TITLE || "Notification";
                            const body = mi?.body || mi?.MESSAGE || "You have a new notification.";
                            const key = `${notification.notification_id}-${index}`;
                            const bgColor = notification.notification_read_status === "0" ? "bg-gray-100" : "bg-white";
                            const currentStatus = notification.workflow_current_step_details?.current_status;
                            const statusInfo = getNotificationStatusLabel(currentStatus);

                            return (
                                <li
                                    key={key}
                                    className={`flex items-start space-x-3 py-3 px-4 ${bgColor}`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    {notification.requestor?.image_url ? (
                                        <img
                                            src={notification.requestor.image_url}
                                            alt={notification.requestor.emp_name}
                                            className="w-10 h-10 rounded-full object-cover"
                                        />
                                    ) : (
                                        <User className="w-10 h-10 text-gray-500" />
                                    )}
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900">{title}</h3>
                                        <p className="text-sm text-gray-700">{body}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {DateTimeUtil.FormatWithDayjsUtc(
                                                DateTimeUtil.convertUtcToLocalTime(notification!.notification_date_time!),
                                                DateTimeUtil.DATETIME_FANCY_2
                                            ) || "N/A"}{" "}
                                            |{" "}
                                            <span className={`${statusInfo.color}`}>
                                                {notification?.workflow_current_step_details?.status_text}
                                            </span>
                                        </p>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                    {/* Sentinel element for IntersectionObserver to detect when to load more */}
                    {notifications.length > 0 && !hasReachedEnd && !isNotificationsLoading && (
                        <div 
                            ref={(el) => {
                                loadMoreSentinelRef.current = el;
                                if (el) {
                                    console.log('[Sentinel] Sentinel element rendered:', {
                                        element: el,
                                        notificationsCount: notifications.length,
                                        totalCount: notificationsMeta?.count ?? 0,
                                        hasReachedEnd: hasReachedEndRef.current
                                    });
                                }
                            }}
                            className="h-1 w-full" 
                            aria-hidden="true"
                        />
                    )}
                    {isNotificationsLoading && notifications.length > 0 && (
                        <div className="flex justify-center items-center py-4">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-600" />
                        </div>
                    )}
                </React.Fragment>
            )}
        </div>
    );
};

export default NotificationsMobile;
